import React, { useContext, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { t } from 'i18next';
import { useNavigate, useParams, matchPath, useLocation } from 'react-router-dom';
import routesMap from 'src/js/router/helpers/routes-map';
import redirectUnauthorizedUser from 'src/js/helpers/redirect-unauthorized-user';
import { SiteSetupContext } from '../modules/site-setup/helpers/site-setup-context';
import getSettingsFromContext from '../modules/site-setup/helpers/get-settings-from-context';
import UseTemplate from '../components/use-template';
import createSite from '../modules/templates/actions/create-site';
import getUserAuthorizationStatus from '../modules/user/selectors/user-authorization-status';
import useSiteProperties from '../modules/templates/hooks/use-site-properties';
import getSiteIdToSwitch from '../modules/sites/selectors/site-id-to-switch-template';

const UseTemplateContainer = () => {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const context = useContext(SiteSetupContext);
  const siteProperties = useSiteProperties();
  const { getData, updateData } = context;
  const dispatch = useDispatch();
  const { templateSlug, archetype, siteName, locale } = getData();
  const isUserAuthenticated = useSelector(getUserAuthorizationStatus);
  const siteIdToSwitch = useSelector(getSiteIdToSwitch);

  useEffect(() => {
    if (params.templateSlug) {
      if (isUserAuthenticated) {
        updateData({
          templateSlug: params.templateSlug,
          useTemplateFlow: true,
        });

        navigate(routesMap.siteName.url(), { replace: true });
      } else {
        redirectUnauthorizedUser(routesMap.useTemplate.url({ templateSlug: params.templateSlug }));
      }
    }
  }, [isUserAuthenticated, navigate, updateData, params.templateSlug]);

  if (!siteIdToSwitch && isUserAuthenticated) {
    if (!templateSlug) {
      navigate(routesMap.siteCategory.url(), { replace: true });
      return null;
    }

    const onSiteNameStep = matchPath(
      location.pathname.replace('/create', ''),
      routesMap.siteName.pattern()
    );

    if (!archetype && !onSiteNameStep) {
      navigate(routesMap.siteName.url(), { replace: true });
    }
  }

  if (!siteProperties) return null;

  const createSiteByTemplate = (selectedTemplateSlug, currentLocale) => {
    const settings = getSettingsFromContext(context);
    const name = siteName || t('My website');

    dispatch(
      createSite({
        name,
        templateSlug: selectedTemplateSlug,
        locale: currentLocale,
        properties: siteProperties,
        settings,
      })
    );

    let match = null;
    const step = Object.keys(routesMap).find((route) => {
      if (route === 'index' || route === 'createSite') return false;
      match = matchPath(location.pathname.replace('/create', ''), routesMap[route].pattern());

      return Boolean(match);
    });

    if (match) {
      navigate(routesMap[step].url(match.params), { replace: true });
    } else {
      navigate(routesMap.siteCategory.url(), { replace: true });
    }
  };

  return (
    <UseTemplate
      templateSlug={templateSlug}
      locale={locale}
      createSiteByTemplate={createSiteByTemplate}
    />
  );
};

export default UseTemplateContainer;
